import * as React from "react";
import { cn } from "@/lib/utils";

/**
 * Both tiles forward every native button attribute (`aria-label`, `aria-current`, `title`,
 * `data-testid`), because a tile IS a button and a screen reader needs the name the screen means,
 * not whatever text happens to be visible inside it.
 *
 * The item tile (DESIGN.md §7): paper, stroke.weak, radius 8, 96 px minimum,
 * name in 18, price in 20 tabular bottom-right. Selected takes fill.selected
 * and stroke.brand. Unavailable is text.disabled and never a strike-through.
 *
 * Every pack's grid is this tile: dishes, rooms, treatments, classes, SKUs.
 */
export function ItemTile({
  name,
  price,
  badge,
  meta,
  selected = false,
  unavailable = false,
  onClick,
  className,
  ...rest
}: Readonly<
  Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "name" | "type" | "disabled" | "onClick"> & {
    name: React.ReactNode;
    price?: React.ReactNode;
    /** The FSSAI glyph, a photo, or a pack's own corner mark. */
    badge?: React.ReactNode;
    meta?: React.ReactNode;
    selected?: boolean;
    unavailable?: boolean;
    onClick?: () => void;
    className?: string;
  }
>) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={unavailable}
      aria-pressed={selected}
      {...rest}
      className={cn(
        "flex min-h-[96px] w-full flex-col justify-between gap-2 rounded-tile border p-4 text-left",
        "transition-colors duration-150 ease-counter",
        selected ? "border-stroke-brand bg-fill-selected" : "border-stroke-weak bg-bg-base",
        unavailable && "cursor-not-allowed text-text-disabled",
        className,
      )}
    >
      <span className="flex items-start justify-between gap-2">
        <span className={cn("font-text text-lg", unavailable ? "text-text-disabled" : "text-text-strong")}>
          {name}
        </span>
        {badge ? <span className="shrink-0">{badge}</span> : null}
      </span>
      <span className="flex items-end justify-between gap-2">
        {meta ? <span className="font-sans text-sm text-text-weak">{meta}</span> : <span />}
        {price ? <span className="font-ledger tabular-nums text-xl">{price}</span> : null}
      </span>
    </button>
  );
}

/**
 * Status lives on the object (DESIGN.md §1, §7): a table, room, chair or bay
 * carries its own state as fill plus word plus icon. There are no banners and
 * no toast for state, in any pack.
 */
export type ObjectState = "free" | "occupied" | "held" | "billed" | "attention";

const STATE_FILL: Record<ObjectState, string> = {
  free: "bg-bg-base border-stroke-weak",
  occupied: "bg-fill-selected border-stroke-brand",
  held: "bg-fill-notice-weak border-stroke-weak",
  billed: "bg-fill-accent-weak border-stroke-weak",
  attention: "bg-fill-error-weak border-stroke-error",
};

const STATE_TEXT: Record<ObjectState, string> = {
  free: "text-text-weak",
  occupied: "text-text-strong",
  held: "text-text-strong",
  billed: "text-honey-800",
  attention: "text-text-error",
};

export function StatusTile({
  name,
  state,
  stateLabel,
  icon,
  badge,
  meta,
  onClick,
  className,
  ...rest
}: Readonly<
  Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "name" | "type" | "onClick"> & {
  name: React.ReactNode;
  state: ObjectState;
  /** The word that pairs with the colour; never colour alone (DESIGN.md §9). */
  stateLabel: string;
  icon?: React.ReactNode;
  /**
   * A corner mark on the object itself — "reserved", "VIP", "due out", a KOT
   * chip. It sits on the object because that is where a cashier looks; the
   * Ledger Ink habit was to rotate an oxblood stamp over the tile, and this is
   * what replaced it.
   */
  badge?: React.ReactNode;
  meta?: React.ReactNode;
  onClick?: () => void;
  className?: string;
  }
>) {
  return (
    <button
      type="button"
      onClick={onClick}
      {...rest}
      className={cn(
        "flex min-h-[96px] w-full flex-col justify-between gap-2 rounded-tile border p-4 text-left",
        "transition-colors duration-150 ease-counter",
        STATE_FILL[state],
        className,
      )}
    >
      <span className="flex items-start justify-between gap-2">
        <span className="font-text text-lg text-text-strong">{name}</span>
        {badge ? <span className="shrink-0">{badge}</span> : null}
      </span>
      <span className={cn("flex items-center gap-1.5 font-sans text-sm font-semibold", STATE_TEXT[state])}>
        {icon ? <span className="shrink-0">{icon}</span> : null}
        {stateLabel}
      </span>
      {meta ? <span className="font-sans text-sm text-text-weak">{meta}</span> : null}
    </button>
  );
}
